// src/components/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { Upload, FileText, Check, AlertTriangle, XCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import { useTheme } from '../contexts/ThemeContext';

function readRuns() {
  try {
    const raw = localStorage.getItem('pdfResults');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function runStatus(run) {
  const results = Object.values(run.data || {});
  if (!results.length) return 'unknown';
  if (results.some((r) => r && r.error)) return 'failed';
  if (results.some((r) => r && r._meta && r._meta.validation && r._meta.validation.status === 'warnings')) return 'warnings';
  return 'ok';
}

function runPages(run) {
  return Object.values(run.data || {}).reduce((sum, r) => {
    const p = r && r._meta && parseInt(r._meta.pages_processed, 10);
    return sum + (Number.isFinite(p) ? p : 0);
  }, 0);
}

function runSeconds(run) {
  const times = Object.values(run.data || {})
    .map((r) => r && r._meta && parseFloat(r._meta.processing_time))
    .filter((t) => Number.isFinite(t));
  return times.length ? times.reduce((a, b) => a + b, 0) : null;
}

function formatWhen(ts) {
  const d = new Date(ts);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default function Dashboard() {
  const [runs, setRuns] = useState([]);
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  useEffect(() => {
    setRuns(readRuns());
  }, []);

  const filesProcessed = runs.reduce((s, r) => s + (r.fileCount || 0), 0);
  const pagesProcessed = runs.reduce((s, r) => s + runPages(r), 0);
  const failedRuns = runs.filter((r) => runStatus(r) === 'failed').length;
  const totalSeconds = runs.reduce((s, r) => s + (runSeconds(r) || 0), 0);
  const avgSeconds = runs.length && totalSeconds ? (totalSeconds / runs.length).toFixed(1) : null;

  const card = darkMode
    ? 'bg-gray-800 border border-gray-700'
    : 'bg-white border border-[#1B5F6E]/10';
  const textMain = darkMode ? 'text-white' : 'text-gray-900';
  const textSub = darkMode ? 'text-gray-400' : 'text-gray-500';

  const statusBadge = (status) => {
    const map = {
      ok: { label: 'OK', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', icon: Check },
      warnings: { label: 'Warnings', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', icon: AlertTriangle },
      failed: { label: 'Failed', cls: 'bg-red-500/10 text-red-600 dark:text-red-400', icon: XCircle },
      unknown: { label: '—', cls: 'bg-gray-500/10 text-gray-500', icon: FileText },
    };
    const s = map[status] || map.unknown;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>
        <s.icon className="w-3 h-3" />
        {s.label}
      </span>
    );
  };

  const stats = [
    { label: 'Documents', value: String(filesProcessed) },
    { label: 'Pages read', value: String(pagesProcessed) },
    { label: 'Runs', value: String(runs.length) + (failedRuns ? ` · ${failedRuns} failed` : '') },
    { label: 'Avg. time / run', value: avgSeconds ? `${avgSeconds}s` : '—' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className={`text-2xl font-bold ${textMain}`}>Dashboard</h2>
        <Button onClick={() => navigate('/')} className="bg-[#1B5F6E] hover:bg-[#164E5A] text-white">
          <Upload className="w-4 h-4 mr-2" />
          New upload
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className={`rounded-xl p-5 ${card}`}>
            <p className={`text-xs font-medium uppercase tracking-wide ${textSub}`}>{s.label}</p>
            <p className={`mt-2 text-2xl font-bold ${textMain}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Recent runs */}
      <div className={`rounded-xl overflow-hidden ${card}`}>
        <div className={`flex items-center justify-between px-5 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-[#1B5F6E]/10'}`}>
          <h3 className={`font-semibold ${textMain}`}>Recent runs</h3>
          {runs.length > 0 && (
            <button
              onClick={() => { if (window.confirm('Delete all saved results?')) { localStorage.removeItem('pdfResults'); setRuns([]); } }}
              className={`text-xs ${textSub} hover:text-red-500`}
            >
              Clear history
            </button>
          )}
        </div>

        {runs.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <FileText className={`w-10 h-10 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${textSub}`}>Nothing processed yet. Upload a PDF to see it here.</p>
            <Button onClick={() => navigate('/')} className="mt-4 bg-[#1B5F6E] hover:bg-[#164E5A] text-white">
              <Upload className="w-4 h-4 mr-2" />
              Upload PDFs
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left text-xs uppercase tracking-wide ${textSub}`}>
                <th className="px-5 py-3 font-medium">File</th>
                <th className="px-5 py-3 font-medium hidden sm:table-cell">Files</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">Pages</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">Time</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">When</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {runs.slice(0, 10).map((run) => (
                <tr
                  key={run.timestamp}
                  className={`border-t ${darkMode ? 'border-gray-700/70 hover:bg-gray-700/40' : 'border-[#1B5F6E]/5 hover:bg-[#1B5F6E]/[0.03]'}`}
                >
                  <td className={`px-5 py-3 font-medium truncate max-w-[240px] ${textMain}`} title={run.filename}>
                    {run.filename}
                  </td>
                  <td className={`px-5 py-3 hidden sm:table-cell ${textSub}`}>{run.fileCount}</td>
                  <td className={`px-5 py-3 hidden md:table-cell ${textSub}`}>{runPages(run)}</td>
                  <td className={`px-5 py-3 hidden md:table-cell ${textSub}`}>
                    {runSeconds(run) !== null ? `${runSeconds(run).toFixed(1)}s` : '—'}
                  </td>
                  <td className="px-5 py-3">{statusBadge(runStatus(run))}</td>
                  <td className={`px-5 py-3 whitespace-nowrap ${textSub}`}>{formatWhen(run.timestamp)}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => navigate('/results', { state: { results: run.data, fromHistory: true } })}
                      className={`inline-flex items-center gap-1 text-xs font-medium ${darkMode ? 'text-[#6fc3d4] hover:text-[#8fd8e6]' : 'text-[#1B5F6E] hover:text-[#164E5A]'}`}
                    >
                      View
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
